/**
 * Groups Service
 * 
 * Higher-level operations for group management including:
 * - Group membership management
 * - Bulk operations
 * - Group hierarchy queries
 */

import { getSupabaseClient, isSupabaseConfigured } from './supabaseClient';

// ============================================================================
// Types
// ============================================================================

export interface GroupMembership {
  id: string;
  userId: string;
  groupId: string;
  role: 'member' | 'admin' | 'owner';
  joinedAt: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface GroupMember {
  id: string;
  membershipId: string;
  userId: string;
  userName: string;
  userEmail?: string;
  role: 'member' | 'admin' | 'owner';
  joinedAt: Date;
}

export interface CreateMembershipInput {
  userId: string;
  groupId: string;
  role?: 'member' | 'admin' | 'owner';
}

export interface GroupWithMembers {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  members: GroupMember[];
  memberCount: number;
}

// ============================================================================
// Membership CRUD
// ============================================================================

/**
 * Get all memberships, optionally filtered
 */
export async function getMemberships(filters?: {
  userId?: string;
  groupId?: string;
}): Promise<GroupMembership[]> {
  if (!isSupabaseConfigured()) {
    console.warn('[GroupsService] Supabase not configured');
    return [];
  }
  
  const supabase = getSupabaseClient();
  let query = supabase.from('memberships').select('*');
  
  if (filters?.userId) {
    query = query.eq('user_id', filters.userId);
  }
  if (filters?.groupId) {
    query = query.eq('group_id', filters.groupId);
  }
  
  const { data, error } = await query.order('joined_at', { ascending: false });
  
  if (error) {
    console.error('[GroupsService] Failed to fetch memberships:', error);
    throw error;
  }
  
  return (data || []).map(row => ({
    id: row.id,
    userId: row.user_id,
    groupId: row.group_id,
    role: row.role,
    joinedAt: new Date(row.joined_at),
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
  }));
}

/**
 * Add a user to a group
 */
export async function addUserToGroup(
  input: CreateMembershipInput
): Promise<GroupMembership> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }
  
  const supabase = getSupabaseClient();
  
  // Check if membership already exists
  const { data: existing } = await supabase
    .from('memberships')
    .select('id')
    .eq('user_id', input.userId)
    .eq('group_id', input.groupId)
    .single();
  
  if (existing) {
    throw new Error('User is already a member of this group');
  }
  
  const { data, error } = await supabase
    .from('memberships')
    .insert({
      user_id: input.userId,
      group_id: input.groupId,
      role: input.role || 'member',
      joined_at: new Date().toISOString(),
    })
    .select()
    .single();
  
  if (error) {
    console.error('[GroupsService] Failed to add user to group:', error);
    throw error;
  }
  
  return {
    id: data.id,
    userId: data.user_id,
    groupId: data.group_id,
    role: data.role,
    joinedAt: new Date(data.joined_at),
    createdAt: new Date(data.created_at),
    updatedAt: new Date(data.updated_at),
  };
}

/**
 * Update a user's role in a group
 */
export async function updateMemberRole(
  membershipId: string,
  role: 'member' | 'admin' | 'owner'
): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }
  
  const supabase = getSupabaseClient();
  
  const { error } = await supabase
    .from('memberships')
    .update({ role, updated_at: new Date().toISOString() })
    .eq('id', membershipId);
  
  if (error) {
    console.error('[GroupsService] Failed to update member role:', error);
    throw error;
  }
}

/**
 * Remove a user from a group
 */
export async function removeUserFromGroup(membershipId: string): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }
  
  const supabase = getSupabaseClient();
  
  const { error } = await supabase
    .from('memberships')
    .delete()
    .eq('id', membershipId);
  
  if (error) {
    console.error('[GroupsService] Failed to remove user from group:', error);
    throw error;
  }
}

/**
 * Remove a user from a group by user ID and group ID
 */
export async function removeUserFromGroupByIds(
  userId: string,
  groupId: string
): Promise<void> {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase not configured');
  }
  
  const supabase = getSupabaseClient();
  
  const { error } = await supabase
    .from('memberships')
    .delete()
    .eq('user_id', userId)
    .eq('group_id', groupId);
  
  if (error) {
    console.error('[GroupsService] Failed to remove user from group:', error);
    throw error;
  }
}

// ============================================================================
// Query Functions
// ============================================================================

/**
 * Get all groups a user belongs to
 */
export async function getUserGroups(userId: string): Promise<{
  groupId: string;
  groupName: string;
  groupDisplayName: string;
  role: string;
  joinedAt: Date;
}[]> {
  if (!isSupabaseConfigured()) {
    return [];
  }
  
  const supabase = getSupabaseClient();
  
  const { data, error } = await supabase
    .from('memberships')
    .select('id,role,joined_at,groups:group_id(id,name)')
    .eq('user_id', userId);
  
  if (error) {
    console.error('[GroupsService] Failed to get user groups:', error);
    return [];
  }
  
  return (data || []).map(row => {
    const group = row.groups as unknown as { id: string; name: string };
    return {
      groupId: group.id,
      groupName: group.name,
      groupDisplayName: group.name,
      role: row.role,
      joinedAt: new Date(row.joined_at),
    };
  });
}

/**
 * Get all members of a group
 */
export async function getGroupMembers(groupId: string): Promise<GroupMember[]> {
  if (!isSupabaseConfigured()) {
    return [];
  }
  
  const supabase = getSupabaseClient();
  
  const { data, error } = await supabase
    .from('memberships')
    .select(`
      id,
      role,
      joined_at,
      users:user_id (
        id,
        name,
        email
      )
    `)
    .eq('group_id', groupId);
  
  if (error) {
    console.error('[GroupsService] Failed to get group members:', error);
    return [];
  }
  
  return (data || []).map(row => {
    const user = row.users as unknown as { id: string; name: string; email?: string };
    return {
      id: user.id,
      membershipId: row.id,
      userId: user.id,
      userName: user.name,
      userEmail: user.email,
      role: row.role as 'member' | 'admin' | 'owner',
      joinedAt: new Date(row.joined_at),
    };
  });
}

/**
 * Get group with all its members
 */
export async function getGroupWithMembers(groupId: string): Promise<GroupWithMembers | null> {
  if (!isSupabaseConfigured()) {
    return null;
  }
  
  const supabase = getSupabaseClient();
  
  // Get group details
  const { data: group, error: groupError } = await supabase
    .from('groups')
    .select('*')
    .eq('id', groupId)
    .single();
  
  if (groupError || !group) {
    console.error('[GroupsService] Failed to get group:', groupError);
    return null;
  }
  
  // Get members
  const members = await getGroupMembers(groupId);
  
  return {
    id: group.id,
    name: group.name,
    displayName: group.display_name,
    description: group.description,
    members,
    memberCount: members.length,
  };
}

/**
 * Check if a user is a member of a group
 */
export async function isUserInGroup(userId: string, groupId: string): Promise<boolean> {
  if (!isSupabaseConfigured()) {
    return false;
  }
  
  const supabase = getSupabaseClient();
  
  const { data } = await supabase
    .from('memberships')
    .select('id')
    .eq('user_id', userId)
    .eq('group_id', groupId)
    .limit(1);
  
  return (data?.length || 0) > 0;
}

/**
 * Get a user's role in a specific group
 */
export async function getUserRoleInGroup(
  userId: string,
  groupId: string
): Promise<'member' | 'admin' | 'owner' | null> {
  if (!isSupabaseConfigured()) {
    return null;
  }
  
  const supabase = getSupabaseClient();
  
  const { data } = await supabase
    .from('memberships')
    .select('role')
    .eq('user_id', userId)
    .eq('group_id', groupId)
    .single();
  
  return data?.role || null;
}

// ============================================================================
// Bulk Operations
// ============================================================================

/**
 * Add multiple users to a group at once
 */
export async function bulkAddUsersToGroup(
  groupId: string,
  userIds: string[],
  role: 'member' | 'admin' | 'owner' = 'member'
): Promise<{ success: number; failed: number; errors: string[] }> {
  const results = { success: 0, failed: 0, errors: [] as string[] };
  
  for (const userId of userIds) {
    try {
      await addUserToGroup({ userId, groupId, role });
      results.success++;
    } catch (error) {
      results.failed++;
      results.errors.push(`User ${userId}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  return results;
}

/**
 * Remove multiple users from a group at once
 */
export async function bulkRemoveUsersFromGroup(
  groupId: string,
  userIds: string[]
): Promise<{ success: number; failed: number; errors: string[] }> {
  const results = { success: 0, failed: 0, errors: [] as string[] };
  
  for (const userId of userIds) {
    try {
      await removeUserFromGroupByIds(userId, groupId);
      results.success++;
    } catch (error) {
      results.failed++;
      results.errors.push(`User ${userId}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  return results;
}

/**
 * Add a user to multiple groups at once
 */
export async function bulkAddUserToGroups(
  userId: string,
  groupIds: string[],
  role: 'member' | 'admin' | 'owner' = 'member'
): Promise<{ success: number; failed: number; errors: string[] }> {
  const results = { success: 0, failed: 0, errors: [] as string[] };
  
  for (const groupId of groupIds) {
    try {
      await addUserToGroup({ userId, groupId, role });
      results.success++;
    } catch (error) {
      results.failed++;
      results.errors.push(`Group ${groupId}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
  
  return results;
}

// ============================================================================
// Analytics
// ============================================================================

/**
 * Get group statistics
 */
export async function getGroupStats(): Promise<{
  totalGroups: number;
  totalMemberships: number;
  groupsWithMembers: number;
  emptyGroups: number;
  averageMembersPerGroup: number;
}> {
  if (!isSupabaseConfigured()) {
    return {
      totalGroups: 0,
      totalMemberships: 0,
      groupsWithMembers: 0,
      emptyGroups: 0,
      averageMembersPerGroup: 0,
    };
  }
  
  const supabase = getSupabaseClient();
  
  // Get group count
  const { count: groupCount } = await supabase
    .from('groups')
    .select('*', { count: 'exact', head: true });
  
  // Get membership count
  const { count: membershipCount } = await supabase
    .from('memberships')
    .select('*', { count: 'exact', head: true });
  
  // Get groups with at least one member
  const { data: groupsWithMembers } = await supabase
    .from('memberships')
    .select('group_id')
    .limit(1000);
  
  const uniqueGroupsWithMembers = new Set(
    (groupsWithMembers || []).map(m => m.group_id)
  ).size;
  
  const totalGroups = groupCount || 0;
  const totalMemberships = membershipCount || 0;
  
  return {
    totalGroups,
    totalMemberships,
    groupsWithMembers: uniqueGroupsWithMembers,
    emptyGroups: totalGroups - uniqueGroupsWithMembers,
    averageMembersPerGroup: totalGroups > 0 
      ? Math.round((totalMemberships / totalGroups) * 10) / 10 
      : 0,
  };
}
